import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, PackageCheck, Truck, Hand, Box } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  Pending: { cls: "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]", next: "Pick", icon: Hand },
  Picking: { cls: "bg-[#007AFF]/10 border-[#007AFF]/30 text-[#4DA3FF]", next: "Pack", icon: Box },
  Packed: { cls: "bg-[#AF52DE]/10 border-[#AF52DE]/30 text-[#C77DFF]", next: "Dispatch", icon: Truck },
  Dispatched: { cls: "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]", next: "Mark Delivered", icon: PackageCheck },
  Delivered: { cls: "bg-white/5 border-white/15 text-zinc-400", next: null, icon: PackageCheck },
};

export default function Queue() {
  const { hubs, selectedHub } = useBrand();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    const q = selectedHub && selectedHub !== "All Hubs" ? `?hub_id=${selectedHub}` : "";
    api.get(`/fulfillment-queue${q}`).then((r) => setRows(r.data)).catch(() => {});
  }, [selectedHub]);
  useEffect(() => { load(); }, [load]);

  const advance = async (row) => {
    try { await api.post(`/fulfillment-queue/${row.order_id}/${row.group_id}/advance`); toast.success(`${row.order_ref} · ${row.hub_name} advanced`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const showHubCol = !selectedHub || selectedHub === "All Hubs";
  const hubName = hubs.find((h) => h.id === selectedHub)?.name;
  const active = rows.filter((r) => r.status !== "Delivered");

  return (
    <div>
      <PageHeader title="Fulfillment Queue" subtitle={showHubCol ? `All hubs · ${active.length} open jobs` : `${hubName || "Hub"} · ${active.length} open jobs`} />
      <div className="p-8">
        {rows.length === 0 ? <EmptyState icon={ClipboardList} title="Queue is clear" hint="Approved orders with reserved stock appear here." /> : (
          <div className="space-y-2">
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.Pending;
              const Icon = meta.icon;
              return (
                <div key={`${r.order_id}-${r.group_id}`} data-testid={`queue-${r.order_id}-${r.group_id}`} className="bg-[#121212] border border-white/10 rounded-sm p-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0 flex items-center gap-4">
                    <Icon size={18} className="text-zinc-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button data-testid={`queue-open-${r.order_id}`} onClick={() => navigate(`/orders/${r.order_id}`)} className="font-mono text-sm text-[#4DA3FF] hover:underline">{r.order_ref}</button>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{r.brand} · {r.channel}</span>
                        {r.priority && r.priority !== "Normal" && <Chip className="bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259]">{r.priority}</Chip>}
                        {showHubCol && <Chip className="bg-white/5 border-white/15 text-zinc-300">{r.hub_name}</Chip>}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-2">
                        <span>{r.customer_name || "—"}</span>
                        {(r.items || []).map((it, i) => <span key={`${it.sku}-${i}`} className="font-mono border border-white/10 rounded-sm px-1.5 py-0.5 text-zinc-400">{it.sku} × {it.qty}</span>)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Chip className={meta.cls}>{r.status}</Chip>
                    {meta.next && (
                      <button data-testid={`queue-advance-${r.order_id}-${r.group_id}`} onClick={() => advance(r)} className="flex items-center gap-1.5 text-xs bg-white text-black font-medium px-3 py-2 rounded-sm hover:bg-zinc-200 transition-colors">
                        {meta.next}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
